import jwtDecode from 'jwt-decode';
import type { CobliJwtPayload } from '../account/cobler/TemporaryTokenManager';
import { AuthToken } from '../account/session/AuthToken';

/**
 * Baggage OTel com o tamanho da frota (`numberOfDevices`), propagado nas requests
 * (`CobliAxios`, `GraphQLRequester`, `EventSourceRequester`) para observabilidade no backend.
 *
 * O valor é cacheado em localStorage **por fleetId** (muda por frota) sob
 * `cobli-fleet-num-devices`, shape `{ [fleetId]: number }`, escrito via
 * `setFleetNumDevicesBaggage` pelo app quando o `userData` fresco chega. Na leitura, a frota
 * corrente é identificada pelo claim `fleetId` do access token — a mesma fonte que
 * `getAuthHeaders` já usa — então uma request nunca carrega o valor de outra frota, inclusive
 * na janela entre a troca de frota e a chegada do `userData` novo.
 */
const FLEET_NUM_DEVICES_STORAGE_KEY = 'cobli-fleet-num-devices';

type FleetNumDevicesCache = Record<string, number>;

const readCache = (): FleetNumDevicesCache => {
  try {
    const raw = localStorage.getItem(FLEET_NUM_DEVICES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as FleetNumDevicesCache) : {};
  } catch {
    return {};
  }
};

/** Persiste o `numberOfDevices` da frota — chamado pelo app ao receber o `userData` fresco. */
const setFleetNumDevicesBaggage = (fleetId: string, numberOfDevices: number) => {
  if (!fleetId) return;
  try {
    const cache = readCache();
    if (cache[fleetId] === numberOfDevices) return;
    cache[fleetId] = numberOfDevices;
    localStorage.setItem(FLEET_NUM_DEVICES_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // Quota excedida / storage bloqueado: baggage é best-effort, nunca quebra o fluxo.
  }
};

const getCurrentFleetId = (): string | undefined => {
  try {
    const jwt = localStorage.getItem(AuthToken.ACCESS_TOKEN);
    if (!jwt) return undefined;
    return jwtDecode<CobliJwtPayload>(jwt).fleetId;
  } catch {
    return undefined;
  }
};

const getOpenTelemetryBaggageHttpHeaders = (): { [key: string]: string } => {
  const fleetId = getCurrentFleetId();
  const numberOfDevices = fleetId ? readCache()[fleetId] : undefined;
  if (numberOfDevices == null) return {};
  return { 'ot-baggage-cobli-fleet-num-devices': numberOfDevices.toString() };
};

export { getOpenTelemetryBaggageHttpHeaders, setFleetNumDevicesBaggage };
